/* global checkDpi, getLabelDimensions */

const ZPL_DRAW_COMMANDS_RE = /\^(FO|FD|FS|FT|BY|BC|GB|AD|A@)/
const DEFAULT_LABEL_DPI = 203

/**
 * DPI desde el nombre de etiqueta.
 * Definida en public/js/functions.js (script en index.html antes del bundle).
 */
function resolveLabelDpi(labelName) {
    if (typeof checkDpi === 'function') return checkDpi(labelName)
    return DEFAULT_LABEL_DPI
}

/**
 * Dimensiones en mm desde ZPL (^PW/^LL).
 * Definida en public/js/functions.js (script en index.html antes del bundle).
 */
function resolveLabelDimensions(zpl, dpi) {
    if (typeof getLabelDimensions === 'function') return getLabelDimensions(zpl, dpi)
    return { width: null, height: null }
}

export function createPrinterStoreAccessors(store) {
    return {
        getTemplates: (formatId) => store.getters['printer/getTemplates'](formatId),
        getCurrentPrinter: () => store.getters['printer/getCurrentPrinter'],
        getPrinterConfig: (configId) => store.getters['printer/getPrinterConfig'](configId),
        getLabels: (labelId) => store.getters['printer/getLabels'](labelId),
        getTemplatesByPrinterModel: (model) => store.getters['printer/getTemplatesByPrinterModel'](model),
        getModels: (model) => store.getters['printer/getModels'](model),
        getIsApp: () => store.getters.getIsApp
    }
}

export function isValidZPL(zpl) {
    if (typeof zpl !== 'string') return false
    const trimmed = zpl.trim()
    if (!trimmed.startsWith('^XA') || !trimmed.endsWith('^XZ')) return false
    return ZPL_DRAW_COMMANDS_RE.test(trimmed)
}

/**
 * Flujo de impresión de etiquetas (print.vue): si el template no es del modelo
 * de impresora actual, usa la variante hija con el mismo parent_id (sin filtros extra).
 */
export function resolveFormatForPrint(formatId, storeAccessors) {
    const baseFormat = storeAccessors.getTemplates(formatId)
    if (!baseFormat) return null

    const currentConfigId = storeAccessors.getCurrentPrinter()
    if (!currentConfigId) return baseFormat

    const currentPrinterConfig = storeAccessors.getPrinterConfig(currentConfigId)
    if (!currentPrinterConfig || !currentPrinterConfig.model) return baseFormat

    if (String(baseFormat.printer_id) === String(currentPrinterConfig.model)) {
        return baseFormat
    }

    const templates = storeAccessors.getTemplatesByPrinterModel(currentPrinterConfig.model)
    let format = baseFormat
    for (const t in templates) {
        const tpl = templates[t]
        if (String(tpl.parent_id) === String(format.id)) {
            format = Object.assign({}, tpl)
        }
    }
    return format
}

/**
 * Timers / productos: variante solo si cumple reglas de app nativa, .z, Brother, etc.
 * (misma lógica que products.vue / TimerProduct).
 */
export function resolveFormatForPrinterVariant(formatId, storeAccessors, currentPrinterConfig = null) {
    const baseFormat = storeAccessors.getTemplates(formatId)
    if (!baseFormat) return null

    if (!currentPrinterConfig) {
        const currentConfigId = storeAccessors.getCurrentPrinter()
        currentPrinterConfig = currentConfigId ? storeAccessors.getPrinterConfig(currentConfigId) : null
    }

    if (!currentPrinterConfig || !currentPrinterConfig.model) {
        return baseFormat
    }

    const baseLabel = storeAccessors.getLabels(baseFormat.label_id)
    if (
        baseLabel &&
        currentPrinterConfig.model &&
        String(baseLabel.printer_id) === String(currentPrinterConfig.model)
    ) {
        return baseFormat
    }

    const templates = storeAccessors.getTemplatesByPrinterModel(currentPrinterConfig.model)
    const printerMeta = storeAccessors.getModels(currentPrinterConfig.model) || {}
    const printerTemplates = currentPrinterConfig.templates
    let format = baseFormat

    for (const t in templates) {
        const tpl = templates[t]
        const label = storeAccessors.getLabels(tpl.label_id)
        const hasAndroidTemplate =
            printerTemplates &&
            currentPrinterConfig.platform === 'android' &&
            typeof printerTemplates[tpl.label_id] !== 'undefined'
        if (
            String(tpl.parent_id) === String(format.id) &&
            (hasAndroidTemplate ||
                (label && label.tpl_file_app && label.tpl_file_app.includes('.z')) ||
                printerMeta.sdk !== 'brother' ||
                !storeAccessors.getIsApp())
        ) {
            format = tpl
        }
    }

    return format
}

export function extractZPLFromPrinter(format, printer, storeAccessors) {
    try {
        if (!format) return null

        const tryLabel = (label) => {
            if (!label || !label.data) return null
            return { zpl: label.data, labelName: label.name || '' }
        }

        if (format.label_id) {
            const direct = tryLabel(storeAccessors.getLabels(format.label_id))
            if (direct) return direct
        }

        const allLabels = storeAccessors.getLabels() || {}
        for (const key in allLabels) {
            const label = allLabels[key]
            if (!label || !label.data) continue
            if (format.label_id && (key === format.label_id || label.id === format.label_id)) {
                return tryLabel(label)
            }
        }

        if (!printer) return null
        const printersMap = printer.printers || printer
        if (!format.printer_id || !printersMap) return null
        const byId = printersMap[format.printer_id]
        if (!byId || !byId.labels) return null
        const first = byId.labels[format.label_id] || null
        if (!first || !first.data) return null
        return { zpl: first.data, labelName: first.name || '' }
    } catch (e) {
        return null
    }
}

export function mergeZplPreviewValues(format, resolvedPreviewFields) {
    let values = {}
    try {
        values = JSON.parse(format && format.data ? format.data : '{}')
    } catch (e) {
        values = {}
    }

    if (!resolvedPreviewFields) return values

    const list = Array.isArray(resolvedPreviewFields)
        ? resolvedPreviewFields
        : typeof resolvedPreviewFields === 'object'
            ? Object.values(resolvedPreviewFields)
            : []

    list.forEach((el) => {
        if (!el || el.f_id === undefined || el.f_id === null) return
        if (typeof el.value === 'undefined' || el.value === null) return
        const id = String(el.f_id)
        if (!Object.prototype.hasOwnProperty.call(values, id)) return
        values[id] = String(el.value)
    })

    return values
}

export function buildZplPreviewState({ zpl, format, labelName, params, resolvedPreviewFields }) {
    const values = mergeZplPreviewValues(format, resolvedPreviewFields)

    let fallbackWidth = 55
    let fallbackHeight = 50
    if (format && format.realistic_preview) {
        try {
            const rp = JSON.parse(format.realistic_preview)
            if (rp && typeof rp.width !== 'undefined') fallbackWidth = rp.width
            if (rp && typeof rp.height !== 'undefined') fallbackHeight = rp.height
        } catch (e) {
            // mantener valores por defecto
        }
    }

    const dpi = resolveLabelDpi(labelName)
    const dimensions = resolveLabelDimensions(zpl, dpi)

    return {
        zpl,
        density: dpi,
        labelWidth: dimensions.width != null ? dimensions.width : fallbackWidth,
        labelHeight: dimensions.height != null ? dimensions.height : fallbackHeight,
        zplValues: values,
        zplParams: params || {}
    }
}
